import { yupResolver } from "@hookform/resolvers/yup";
import moment from "moment";
import { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import Select, { Theme, StylesConfig } from "react-select";
import { SelectFetch } from "react-select-fetch";
import * as XLSX from "xlsx";
import * as yup from "yup";

import Button from "components/Button";
import DatePicker from "components/DatePicker";
import Input from "components/Input";
import { useAuth } from "providers/Auth";
import { useCompany } from "providers/Company";
import { useProfessionalQuote } from "providers/Quote/ProfessionalQuote";
import { api } from "services";
import { RoleDescription } from "types/enums";
import { ReportSearchData } from "types/quotes";
import { generateCnab240, CnabPayment, CnabCompany } from "utils/cnab240";
import {
  creationDateBounds,
  defaultReportPeriod,
  fetchConcludedQuotes,
  filterAndSortConcluded,
} from "utils/fetchConcludedQuotes";
import { getCompanyOptions } from "utils/getOptions";
import { toast } from "utils/toast";

import * as C from "./styles";

const customTheme = (theme: Theme) => ({
  ...theme,
  borderRadius: 7,
  colors: {
    ...theme.colors,
    danger: "var(--danger)",
    dangerLight: "var(--danger-light)",
    neutral0: "var(--neutral-0)",
    neutral5: "var(--neutral-5)",
    neutral10: "var(--neutral-10)",
    neutral20: "var(--neutral-20)",
    neutral30: "var(--neutral-30)",
    neutral40: "var(--neutral-40)",
    neutral50: "var(--neutral-50)",
    neutral60: "var(--neutral-60)",
    neutral70: "var(--neutral-70)",
    neutral80: "var(--neutral-80)",
    neutral90: "var(--neutral-90)",
    primary: "var(--primary)",
    primary25: "var(--primary-25)",
    primary50: "var(--primary-50)",
    primary75: "var(--primary-75)",
  },
});

const ProfessionalQuoteReportSearch = () => {
  const { userRole } = useAuth();
  const { isLoadingProfessionalCompanies, professionalCompanies } =
    useCompany();
  const { reportSearchData, setReportSearchData } = useProfessionalQuote();

  const [openDropdown, setOpenDropdown] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingCnab, setIsExportingCnab] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<{
    value: number;
    label: string;
  } | null>(null);

  const customStyles: StylesConfig = {
    control: (base: Record<string, unknown>) => ({
      ...base,
      height: 48,
      borderColor: "var(--border)",
      cursor: "pointer",
      "&:hover": {},
    }),
    singleValue: (base: Record<string, unknown>) => ({
      ...base,
      textTransform: "uppercase",
    }),
    option: (base: Record<string, unknown>) => ({
      ...base,
      cursor: "pointer",
      textTransform: "uppercase",
    }),
    menu: (base: Record<string, unknown>) => ({
      ...base,
      position: "relative",
      zIndex: 5,
    }),
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const companyId =
        reportSearchData.companyId && reportSearchData.companyId !== -1
          ? reportSearchData.companyId
          : undefined;

      const all = await fetchConcludedQuotes({
        companyId,
        idQuote: reportSearchData.idQuote || undefined,
        patientName: reportSearchData.patientName || undefined,
        ...creationDateBounds(reportSearchData.beginDate, reportSearchData.endDate),
      });
      const quotes = filterAndSortConcluded(
        all,
        reportSearchData.beginDate,
        reportSearchData.endDate
      );

      const fmtDate = (v: string | null | undefined) => {
        if (!v || v.startsWith("0001-01-01")) return "";
        return moment(v).format("DD/MM/YYYY");
      };
      const fmtPrice = (v: number | null | undefined) =>
        v == null ? 0 : Number(v.toFixed(2));

      const rows = quotes.map((q) => {
        const pmt = q.payment;
        const isLink = !!pmt?.linkUrl;
        return {
          "Id Orçamento": q.id,
          "Empresa": q.company?.name ?? "",
          "Prestador": q.professional?.name ?? "",
          "Paciente": q.patient?.name ?? "",
          "Tipo Pagamento": isLink ? "Link de Pagamento" : "Máquina Física",
          "Valor Total (R$)": fmtPrice(pmt?.value),
          "Custo CET (R$)": fmtPrice(pmt?.cetCost),
          "Parcelas": pmt?.parcelQtd ?? 0,
          "Data Pagamento": fmtDate(pmt?.paymentDate),
          "Valor Repasse (R$)": fmtPrice(pmt?.compensationValue),
          "Data Repasse": fmtDate(pmt?.paymentLiquidationDate),
          "Código Pagamento": pmt?.orderId ?? "",
        };
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [
        { wch: 13 }, { wch: 30 }, { wch: 30 }, { wch: 30 }, { wch: 20 },
        { wch: 16 }, { wch: 14 }, { wch: 10 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 22 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Orçamentos");
      XLSX.writeFile(wb, `relatorio-orcamentos-${moment().format("YYYY-MM-DD")}.xlsx`);

      toast.fire({ icon: "success", title: `${quotes.length} orçamentos exportados!` });
    } catch (err) {
      console.error("[Export] ERRO:", err);
      toast.fire({ icon: "error", title: "Erro ao exportar. Verifique o console." });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportCnab240 = async () => {
    const companyId = reportSearchData.companyId;
    if (!companyId || companyId === -1) {
      toast.fire({ icon: "warning", title: "Selecione uma empresa nos filtros para exportar CNAB 240." });
      return;
    }

    setIsExportingCnab(true);
    try {
      // 1. Fetch company data
      const companyRes = await api.get(`/Company/${companyId}`, { timeout: 15000 }).catch(() =>
        api.get("/Company/search", { params: { page: 1, size: 100 }, timeout: 15000 })
          .then((r) => ({
            data: (Array.isArray(r.data) ? r.data : []).find((c: any) => c.id === companyId) ?? null,
          }))
      );
      const companyData = companyRes?.data;
      if (!companyData) throw new Error("Empresa não encontrada.");

      const dr = companyData.dataRecipient;
      if (!dr?.agency || !dr?.account) {
        toast.fire({ icon: "error", title: "Dados bancários da empresa não cadastrados (agência/conta)." });
        setIsExportingCnab(false);
        return;
      }

      const company: CnabCompany = {
        cnpj: companyData.cnpj ?? "",
        name: companyData.legalName || companyData.name,
        agency: dr.agency,
        agencyDv: dr.accountDigit ?? " ",
        account: dr.account,
        accountDv: dr.accountDigit ?? " ",
        agencyAccountDv: " ",
      };

      // 2. Fetch all professionals for this company (to get CPF)
      const profMap: Record<number, { cpf: string; email: string; phone: string }> = {};
      let profPage = 1;
      while (true) {
        const profRes = await api.get("/Professional/search", {
          params: { page: profPage, size: 200, companyId, active: true },
          timeout: 30000,
        });
        const profs: any[] = Array.isArray(profRes.data) ? profRes.data : [];
        for (const p of profs) {
          profMap[p.id] = {
            cpf: p.cpf ?? "",
            email: p.email ?? "",
            phone: `${p.dddMobilePhone ?? ""}${p.mobilePhone ?? ""}`,
          };
        }
        if (profs.length < 200) break;
        profPage++;
      }

      // 3. Fetch quotes com repasse pendente pra essa empresa (independe dos
      // filtros de período da tela — o CNAB é a remessa bancária completa)
      const allQuotes: any[] = [];
      let page = 1;
      while (true) {
        const res = await api.get("/Quote/search", {
          params: { size: 500, onlyOpened: false, companyId, page },
          timeout: 30000,
        });
        const batch: any[] = Array.isArray(res.data) ? res.data : [];
        allQuotes.push(...batch);
        if (batch.length < 500) break;
        page++;
      }

      // 4. Build CnabPayment list (only quotes with repasse value > 0)
      const payments: CnabPayment[] = allQuotes
        .filter((q) => (q.payment?.compensationValue ?? 0) > 0)
        .map((q) => {
          const profId = q.professional?.id;
          const profInfo = profMap[profId] ?? {};
          return {
            quoteId: q.id,
            professional: {
              name: q.professional?.name ?? "",
              cpf: profInfo.cpf,
              email: profInfo.email,
              phone: profInfo.phone,
            },
            value: q.payment?.compensationValue ?? 0,
            paymentDate: q.payment?.paymentDate ?? null,
          };
        });

      if (payments.length === 0) {
        toast.fire({ icon: "warning", title: "Nenhum orçamento com valor de repasse encontrado." });
        setIsExportingCnab(false);
        return;
      }

      // 5. Generate and download CNAB 240
      const content = generateCnab240(payments, company);
      const blob = new Blob([content], { type: "text/plain;charset=ascii" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cnab240-${moment().format("YYYYMMDD-HHmmss")}.rem`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.fire({ icon: "success", title: `CNAB 240 gerado com ${payments.length} pagamentos.` });
    } catch (err) {
      console.error("[CNAB240] ERRO:", err);
      toast.fire({ icon: "error", title: "Erro ao gerar CNAB 240. Verifique o console." });
    } finally {
      setIsExportingCnab(false);
    }
  };

  const handleTextInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    setValue("patientName", inputValue);

    if (!inputValue) {
      setReportSearchData({
        ...reportSearchData,
        patientName: "",
      });
    }
  };

  const handleIdQuoteInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    setValue("idQuote", inputValue);

    if (!inputValue) {
      setReportSearchData({
        ...reportSearchData,
        idQuote: null,
      });
    }
  };

  const handleCompanyChange = (newValue: { value: number; label: string } | null) => {
    if (!newValue) {
      setSelectedCompany({ value: -1, label: "" });
      return;
    }

    setSelectedCompany(newValue);
  };

  const clearFilters = () => {
    const period = defaultReportPeriod();
    reset({
      patientName: "",
      idQuote: null,
      beginDate: period.beginDate.format("YYYY-MM-DD"),
      endDate: period.endDate.format("YYYY-MM-DD"),
      companyId: -1,
    });
    setSelectedCompany({ value: -1, label: "" });
    setReportSearchData(period);
  };

  const schema = yup.object().shape({
    patientName: yup.string(),
    idQuote: yup.string().nullable(),
    beginDate: yup.date().typeError("*Data inválida").nullable(),
    endDate: yup.date().typeError("*Data inválida").nullable(),
    companyId: yup.number(),
  });

  const { register, handleSubmit, control, setValue, reset } =
    useForm<ReportSearchData>({
      resolver: yupResolver(schema),
      defaultValues: {
        patientName: reportSearchData.patientName ?? "",
        idQuote: reportSearchData.idQuote ?? "",
        beginDate: reportSearchData.beginDate
          ? moment(reportSearchData.beginDate).format("YYYY-MM-DD")
          : null,
        endDate: reportSearchData.endDate
          ? moment(reportSearchData.endDate).format("YYYY-MM-DD")
          : null,
        companyId: reportSearchData.companyId ?? -1,
      },
    });

  const onSubmit = async (data: ReportSearchData) => {
    setReportSearchData({
      ...data,
      companyId: selectedCompany?.value,
      companyName: selectedCompany?.label,
    });
    setOpenDropdown(false);
  };

  useEffect(() => {
    if (userRole === RoleDescription.Admin) {
      setSelectedCompany(null);
    } else if (professionalCompanies.length > 0) {
      // Profissional/Secretária: nunca busca fora das próprias instituições.
      // Secretária ainda escolhe entre as suas; Profissional puro fica
      // implicitamente restrito (sem seletor) a todas as suas empresas.
      if (userRole === RoleDescription.Secretary) {
        setSelectedCompany({
          value: professionalCompanies[0].id,
          label: professionalCompanies[0].name,
        });
        setValue("companyId", professionalCompanies[0].id);
        setReportSearchData((prev) => ({
          ...prev,
          companyId: professionalCompanies[0].id,
          companyName: professionalCompanies[0].name,
        }));
      }
    }
  }, [professionalCompanies]);

  return (
    <div className="row gx-2">
      <div className="col col-auto pb-2">
        <Button
          className="btn-sm btn-outline-success"
          onClick={handleExportExcel}
          disabled={isExporting}
        >
          {isExporting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Exportando...
            </>
          ) : (
            <>
              <i className="uil uil-file-download mr-2" />
              Exportar Excel
            </>
          )}
        </Button>
      </div>

      {(userRole === RoleDescription.Admin || userRole === RoleDescription.Secretary) && (
        <div className="col col-auto pb-2">
          <Button
            className="btn-sm btn-outline-primary"
            onClick={handleExportCnab240}
            disabled={isExportingCnab}
          >
            {isExportingCnab ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                Gerando CNAB...
              </>
            ) : (
              <>
                <i className="uil uil-file-alt mr-2" />
                Exportar CNAB 240
              </>
            )}
          </Button>
        </div>
      )}

      <form className="col col-auto pb-2" onSubmit={handleSubmit(onSubmit)}>
        <C.Container>
          <Dropdown autoClose={false} show={openDropdown}>
            <Dropdown.Toggle
              id="dropdown-basic"
              variant="dark"
              size="sm"
              onClick={() => setOpenDropdown(!openDropdown)}
            >
              FILTROS
            </Dropdown.Toggle>

            <Dropdown.Menu
              style={{
                width: "90vw",
                maxHeight: "29rem",
                maxWidth: "22rem",
                overflowY: "auto",
                zIndex: 3,
                paddingBottom: 0,
              }}
            >
              <Dropdown.Header className="lh-1">FILTROS</Dropdown.Header>

              <Dropdown.Divider />

              <Dropdown.ItemText>
                <div className="row">
                  <div className="form-group col-12 w-full">
                    <Input
                      label="Nome do Paciente"
                      labelClassName="small text-muted mb-0"
                      type="text"
                      placeholder="Nome do Paciente"
                      register={register("patientName")}
                      onChange={handleTextInput}
                      defaultValue={reportSearchData.patientName}
                    />
                  </div>

                  <div className="form-group col-12 w-full">
                    <Input
                      label="Id do Orçamento"
                      labelClassName="small text-muted mb-0"
                      type="number"
                      placeholder="Id do Orçamento"
                      register={register("idQuote")}
                      onChange={handleIdQuoteInput}
                      defaultValue={reportSearchData.idQuote ?? ""}
                    />
                  </div>

                  {userRole === RoleDescription.Secretary && (
                    <div className="form-group col-12 w-full flex-fill">
                      <label className="small text-muted mb-0">
                        Selecione a Empresa
                      </label>
                      <Controller
                        name="companyId"
                        control={control}
                        render={({ field }) => (
                          <Select
                            options={professionalCompanies.map((company) => ({
                              value: company.id,
                              label: company.name,
                            }))}
                            onChange={(newValue) => {
                              const company = newValue as {
                                value: number;
                                label: string;
                              };

                              field.onChange(company?.value ?? -1);
                              handleCompanyChange(company);
                            }}
                            placeholder="Buscar Instituição"
                            isClearable
                            value={selectedCompany}
                            theme={customTheme}
                            styles={customStyles}
                            isDisabled={isLoadingProfessionalCompanies}
                          />
                        )}
                      ></Controller>
                    </div>
                  )}

                  {userRole === RoleDescription.Admin && (
                    <div className="form-group col-12 w-full flex-fill">
                      <label className="small text-muted mb-0">
                        Selecione a Empresa
                      </label>
                      <Controller
                        name="companyId"
                        control={control}
                        render={({ field }) => (
                          <SelectFetch
                            loadingMessage={() => "Carregando..."}
                            noOptionsMessage={() => "Nenhum resultado encontrado"}
                            theme={customTheme}
                            queryParams={{ limit: 20 }}
                            placeholder="Buscar Instituição"
                            url="Company/search"
                            get={getCompanyOptions}
                            isClearable
                            styles={customStyles}
                            onChange={(newValue) => {
                              const company = newValue as {
                                value: number;
                                label: string;
                              };

                              field.onChange(company?.value ?? -1);
                              handleCompanyChange(company);
                            }}
                            value={selectedCompany}
                          />
                        )}
                      ></Controller>
                    </div>
                  )}

                  <div className="form-group col-6 w-full">
                    <DatePicker
                      label="Período de"
                      labelClassName="small text-muted mb-0"
                      control={control}
                      name="beginDate"
                      defaultValue={null}
                    />
                  </div>

                  <div className="form-group col-6 w-full">
                    <DatePicker
                      label="Período até"
                      labelClassName="small text-muted mb-0"
                      control={control}
                      name="endDate"
                      defaultValue={null}
                    />
                  </div>
                </div>
              </Dropdown.ItemText>
              <Dropdown.ItemText
                className="position-sticky bg-white py-2 border-top"
                style={{ bottom: 0 }}
              >
                <div className="row justify-content-between">
                  <div className="col col-auto">
                    <a
                      className="btn btn-light btn-sm text-uppercase"
                      onClick={clearFilters}
                    >
                      <i className="uil uil-times mr-2" />
                      Limpar Filtros
                    </a>
                  </div>
                  <div className="col col-auto">
                    <Button
                      type="submit"
                      className="btn-primary btn-sm text-uppercase"
                    >
                      Filtrar
                    </Button>
                  </div>
                </div>
              </Dropdown.ItemText>
            </Dropdown.Menu>
          </Dropdown>
        </C.Container>
      </form>
    </div>
  );
};

export default ProfessionalQuoteReportSearch;
